import React from "react";
import CrudManager from "../../common/CrudManager";
import {
  Info,
  Globe,
  Facebook,
  Linkedin,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";

const InformationManager = ({ onClose }) => {
  const getFields = () => [
    {
      name: "key",
      label: "Information Key",
      type: "text",
      placeholder: "e.g., company_email, linkedin_url",
      required: true,
    },
    {
      name: "label",
      label: "Display Label",
      type: "text",
      placeholder: "e.g., Company Email, LinkedIn Profile",
      required: true,
    },
    {
      name: "value",
      label: "Value",
      type: "textarea",
      placeholder: "Enter the value...",
      required: true,
      rows: 3,
      fullWidth: true,
    },
    {
      name: "category",
      label: "Category",
      type: "select",
      required: true,
      options: [
        { value: "contact", label: "Contact" },
        { value: "social", label: "Social Media" },
        { value: "location", label: "Location" },
        { value: "other", label: "Other" },
      ],
    },
    {
      name: "isPublic",
      label: "Public Visibility",
      type: "select",
      required: true,
      options: [
        { value: "true", label: "Public (Visible on website)" },
        { value: "false", label: "Private (Internal only)" },
      ],
    },
  ];

  const formatCreateData = (formData) => ({
    key: formData.key.trim().toLowerCase().replace(/\s+/g, "_"),
    label: formData.label.trim(),
    value: formData.value.trim(),
    category: formData.category,
    isPublic: formData.isPublic === "true",
  });

  const formatUpdateData = (formData) => ({
    label: formData.label.trim(),
    value: formData.value.trim(),
    category: formData.category,
    isPublic: formData.isPublic === "true",
  });

  const getIconByCategory = (category) => {
    switch (category) {
      case "social":
        return <Globe size={24} />;
      case "contact":
        return <Phone size={24} />;
      case "location":
        return <MapPin size={24} />;
      default:
        return <Info size={24} />;
    }
  };

  const renderItem = (info, onEdit, onDelete) => (
    <div className="flex items-center justify-between w-full group">
      <div className="flex items-center space-x-4 flex-1 min-w-0">
        <div className="w-14 h-14 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center text-subidha-blue group-hover:bg-subidha-blue group-hover:text-white transition-all duration-300 flex-shrink-0">
          {getIconByCategory(info.category)}
        </div>

        <div className="space-y-1 flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">
              {info.label}
            </h3>
            <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-blue-50 text-subidha-blue border border-blue-100 uppercase tracking-tighter">
              {info.category}
            </span>
            {info.isPublic ? (
              <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-green-50 text-green-600 border border-green-100 uppercase">
                Public
              </span>
            ) : (
              <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200 uppercase">
                Private
              </span>
            )}
          </div>

          <div className="flex flex-col space-y-0.5">
            <div className="text-[11px] text-slate-400 font-bold font-mono">
              Key: {info.key}
            </div>
            <div className="text-[11px] text-slate-700 font-bold truncate">
              {info.value}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
        <button
          onClick={() => onEdit(info)}
          className="p-2.5 text-slate-400 hover:text-subidha-blue hover:bg-blue-50 rounded-xl transition-all"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
        <button
          onClick={() => onDelete(info)}
          className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <CrudManager
      title="Information Management"
      apiEndpoint="/information"
      fields={getFields}
      renderItem={renderItem}
      onClose={onClose}
      icon={Info}
      emptyMessage="No information entries yet. Add company details!"
      formatCreateData={formatCreateData}
      formatUpdateData={formatUpdateData}
    />
  );
};

export default InformationManager;
